import { useEffect, useState, useCallback } from "react";
import type {
  FocusSessionVariant,
  MmkFocusSessionCategory,
  MmkItem,
} from "../types/mmk";
import { postMmkItemAction } from "../axios/mmk";
import {
  loadQuizState,
  loadFocusSessionState,
  saveFocusSessionState,
  clearFocusSessionState,
} from "../utils/mmkPersistence";
import { trackMmkEvent } from "../utils/mmkAnalytics";

export function getFocusSessionItems(
  items: MmkItem[],
  variant: FocusSessionVariant,
  category?: MmkFocusSessionCategory | "",
): MmkItem[] {
  const eligible = items.filter((i) => !i.is_mastered);
  const pinned = eligible.filter((i) => i.is_pinned);
  const reviewLater = eligible.filter(
    (i) => i.is_review_later && !i.is_pinned,
  );
  const defaults = eligible.filter(
    (i) => !i.is_pinned && !i.is_review_later,
  );
  const ordered = [...pinned, ...reviewLater, ...defaults];

  if (variant === "quick_5") return ordered.slice(0, 5);
  if (variant === "by_category") {
    if (!category) return [];
    if (category === "Personal") {
      return ordered.filter((i) => i.source === "personal");
    }
    return ordered.filter((i) => i.category === category);
  }
  return ordered;
}

// Stable N/M progress for a shrinking remaining-queue focus session.
export function getFocusSessionProgress(
  focusSessionIndex: number,
  remainingCount: number,
  sessionTotal: number,
): { current: number; total: number } {
  const total = Math.max(sessionTotal, remainingCount, 1);
  const completed = Math.max(0, total - remainingCount);
  const current = Math.min(total, completed + focusSessionIndex + 1);
  return { current, total };
}

export function useMmkFocusSession(
  scopeKey: string,
  items: MmkItem[],
  toggleMastered: (id: string) => Promise<void>,
) {
  const [isFocusSession, setIsFocusSession] = useState(false);
  const [focusSessionIndex, setFocusSessionIndex] = useState(0);
  const [focusSessionFlipped, setFocusSessionFlipped] = useState(false);
  const [sessionItems, setSessionItems] = useState<MmkItem[]>([]);
  const [sessionTotal, setSessionTotal] = useState(0);
  const [hasRestoreBeenAttempted, setHasRestoreBeenAttempted] =
    useState(false);

  const startFocusSessionWithItems = useCallback((itemsToUse: MmkItem[]) => {
    setSessionItems(itemsToUse);
    setSessionTotal(itemsToUse.length);
    setIsFocusSession(true);
    setFocusSessionIndex(0);
    setFocusSessionFlipped(false);
    const firstId = itemsToUse[0]?.id;
    if (firstId) postMmkItemAction(firstId, "flip").catch(() => {});
  }, []);

  const handleExitFocusSession = useCallback(() => {
    clearFocusSessionState(scopeKey);
    setIsFocusSession(false);
    setFocusSessionIndex(0);
    setFocusSessionFlipped(false);
    setSessionItems([]);
    setSessionTotal(0);
  }, [scopeKey]);

  const handleFocusSessionPrev = useCallback(() => {
    setFocusSessionIndex((i) => Math.max(0, i - 1));
    setFocusSessionFlipped(false);
    const item = sessionItems[Math.max(0, focusSessionIndex - 1)];
    if (item) postMmkItemAction(item.id, "flip").catch(() => {});
  }, [sessionItems, focusSessionIndex]);

  const handleFocusSessionNext = useCallback(() => {
    setFocusSessionIndex((i) => Math.min(sessionItems.length - 1, i + 1));
    setFocusSessionFlipped(false);
    const item =
      sessionItems[Math.min(sessionItems.length - 1, focusSessionIndex + 1)];
    if (item) postMmkItemAction(item.id, "flip").catch(() => {});
  }, [sessionItems, focusSessionIndex]);

  const handleMarkAsKnown = useCallback(
    (itemId: string) => {
      trackMmkEvent("Click I Know It", {
        item_id: itemId,
        source: "focus_session",
      });
      const newItems = sessionItems.filter((i) => i.id !== itemId);
      if (newItems.length === 0) {
        clearFocusSessionState(scopeKey);
      }
      toggleMastered(itemId);
      setSessionItems(newItems);
      setFocusSessionIndex((i) =>
        Math.min(i, Math.max(0, newItems.length - 1)),
      );
      setFocusSessionFlipped(false);
    },
    [scopeKey, toggleMastered, sessionItems],
  );

  useEffect(() => {
    if (!scopeKey || items.length === 0) return;
    if (loadQuizState(scopeKey)) {
      setHasRestoreBeenAttempted(true);
      return;
    }
    const saved = loadFocusSessionState(scopeKey);
    if (!saved || saved.sessionItemIds.length === 0) {
      setHasRestoreBeenAttempted(true);
      return;
    }
    const restored = saved.sessionItemIds
      .map((id) => items.find((i) => i.id === id))
      .filter((i): i is MmkItem => i != null);
    if (restored.length === 0) {
      setHasRestoreBeenAttempted(true);
      return;
    }
    setIsFocusSession(true);
    setSessionItems(restored);
    setSessionTotal(Math.max(saved.sessionTotal, restored.length));
    setFocusSessionIndex(Math.min(saved.index, restored.length - 1));
    setFocusSessionFlipped(saved.flipped);
    setHasRestoreBeenAttempted(true);
  }, [scopeKey, items]);

  useEffect(() => {
    if (isFocusSession && sessionItems.length > 0) {
      setFocusSessionIndex((i) => Math.min(i, sessionItems.length - 1));
    }
  }, [isFocusSession, sessionItems.length]);

  useEffect(() => {
    if (
      !scopeKey ||
      !isFocusSession ||
      sessionItems.length === 0
    ) {
      return;
    }
    saveFocusSessionState(scopeKey, {
      sessionItemIds: sessionItems.map((i) => i.id),
      sessionTotal: Math.max(sessionTotal, sessionItems.length),
      index: focusSessionIndex,
      flipped: focusSessionFlipped,
    });
  }, [
    scopeKey,
    isFocusSession,
    sessionItems,
    sessionTotal,
    focusSessionIndex,
    focusSessionFlipped,
  ]);

  return {
    isFocusSession,
    sessionItems,
    sessionTotal,
    focusSessionIndex,
    focusSessionFlipped,
    setFocusSessionFlipped,
    hasRestoreBeenAttempted,
    startFocusSessionWithItems,
    handleExitFocusSession,
    handleFocusSessionPrev,
    handleFocusSessionNext,
    handleMarkAsKnown,
  };
}
